import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNowStrict } from "date-fns";
import { Plug, RefreshCw } from "lucide-react";
import { useMemo } from "react";
import {
	EventSourceMark,
	EventSourceTile,
	NOTIFICATION_TILE,
	RowKindLine,
} from "@/components/entity-tabs/events-tab/event-row-parts";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
	type FeedItem,
	useAllFeeds,
	useConnectorDefinitions,
	useTriggerFeed,
} from "@/lib/api/connections";
import type { LobuLensContext } from "@/lib/lens-context";

/** While a run is in flight the row re-reads its feed until it settles. */
const SYNC_POLL_MS = 4000;

function pageOrigin(context: LobuLensContext | undefined): string | null {
	if (context?.kind !== "browser" || !context.url) return null;
	try {
		return new URL(context.url).origin;
	} catch {
		return null;
	}
}

/**
 * Syncable feeds whose connector declares the page in front as one of its
 * browser origins: the side panel's "This page" row with Sync now. The
 * origins come from the connector definition, so every browser connector gets
 * the row without per-connector code.
 */
export function usePageFeeds(
	organizationId: string | null,
	context: LobuLensContext | undefined,
) {
	const origin = pageOrigin(context);
	const { data: definitions = [] } = useConnectorDefinitions(
		origin ? organizationId : null,
	);
	const connectorKeys = useMemo(
		() =>
			new Set(
				definitions
					.filter((d) => origin && d.browser?.origins.includes(origin))
					.map((d) => d.key),
			),
		[definitions, origin],
	);
	const enabled = !!organizationId && connectorKeys.size > 0;
	const { data: feeds = [] } = useQuery({
		...useAllFeeds.queryOptions(organizationId, { status: "active", enabled }),
		enabled,
		refetchInterval: (query) =>
			(query.state.data as FeedItem[] | undefined)?.some(
				(f) => connectorKeys.has(f.connector_key) && f.active_runs > 0,
			)
				? SYNC_POLL_MS
				: false,
	});
	return useMemo(
		() =>
			feeds.filter(
				(f) =>
					connectorKeys.has(f.connector_key) && f.operations.includes("sync"),
			),
		[feeds, connectorKeys],
	);
}

export function syncSummary(feed: FeedItem): { text: string; failed: boolean } {
	if (feed.active_runs > 0) return { text: "Syncing…", failed: false };
	if (feed.last_sync_status === "failed" && feed.last_error)
		return { text: feed.last_error, failed: true };
	if (!feed.last_sync_at)
		return {
			text: "Not synced yet. Sync while you're signed in.",
			failed: false,
		};
	return {
		text: `Last synced ${formatDistanceToNowStrict(new Date(feed.last_sync_at), { addSuffix: true })}.`,
		failed: false,
	};
}

/** One pinned row per matching feed, shaped like a notification row. */
export function PageConnectionRows({
	organizationId,
	feeds,
}: {
	organizationId: string | null;
	feeds: FeedItem[];
}) {
	const { data: connectorDefinitions = [] } =
		useConnectorDefinitions(organizationId);
	const trigger = useTriggerFeed();
	if (feeds.length === 0) return null;
	return (
		<section aria-label="This page" className="space-y-1">
			<ul className="space-y-1">
				{feeds.map((feed) => {
					const summary = syncSummary(feed);
					const syncing =
						feed.active_runs > 0 ||
						(trigger.isPending && trigger.variables === feed.id);
					const title = [
						feed.connection_name ?? feed.connector_name ?? feed.connector_key,
						feed.display_name ?? feed.feed_key,
					].join(" · ");
					return (
						<li key={feed.id} className="rounded-lg bg-primary/5 p-3">
							<div className="flex gap-3">
								<EventSourceTile className={NOTIFICATION_TILE}>
									<EventSourceMark
										source={{ platform: feed.connector_key }}
										connectorDefinitions={connectorDefinitions}
										label={title}
										fallback={<Plug className="h-4 w-4" />}
									/>
								</EventSourceTile>
								<div className="min-w-0 flex-1">
									<h4 className="line-clamp-2 min-w-0 text-sm font-medium">
										{title}
									</h4>
									<p
										className={
											summary.failed
												? "mt-1 line-clamp-3 text-sm text-destructive"
												: "mt-1 text-sm text-muted-foreground"
										}
									>
										{summary.text}
									</p>
									<RowKindLine tag="This page">
										{feed.device_label
											? `Connection · ${feed.device_label}`
											: "Connection"}
									</RowKindLine>
									<div className="mt-2">
										<Button
											type="button"
											size="sm"
											variant={summary.failed ? "default" : "outline"}
											className="h-7 text-xs"
											disabled={syncing}
											onClick={() => trigger.mutate(feed.id)}
										>
											{syncing ? (
												<Spinner size="sm" className="mr-1" />
											) : (
												<RefreshCw className="mr-1 h-3 w-3" />
											)}
											{syncing
												? "Syncing"
												: summary.failed
													? "Try again"
													: "Sync now"}
										</Button>
									</div>
								</div>
							</div>
						</li>
					);
				})}
			</ul>
		</section>
	);
}
